// API para el módulo de Calendario de Empleado
// Centraliza todos los endpoints relacionados con el calendario de vacaciones

import type { AxiosError } from "axios";
import { worktimeApi } from "../worktime";
import type {
  EmployeeCalendar,
  SimpleDateRange,
  SimpleDate,
  ProcedureVacationGroup,
  FullVacation
} from "./employeeCalendarDTO";

/**
 * Callbacks genéricos
 */
type SuccessCallback<T = unknown> = (data: T) => void;
type ErrorCallback = (error: AxiosError) => void;

/**
 * API cliente para calendario de empleado
 */
export class employeeCalendarAPI {
  
  /**
   * Obtiene el calendario del empleado para un rango de fechas
   * @param dateRange Rango de fechas (fromDate y toDate)
   * @param onSuccess Callback de éxito con lista de días de calendario
   * @param onError Callback de error
   */
  static getCalendar(
    dateRange: SimpleDateRange,
    onSuccess: SuccessCallback<EmployeeCalendar[]>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericPost("user/calendar", dateRange, onSuccess, onError);
  }

  /**
   * Obtiene las vacaciones del grupo/equipo del empleado para un mes específico
   * @param year Año (ej: 2024)
   * @param month Mes (1-12)
   * @param onSuccess Callback de éxito con datos agrupados de vacaciones
   * @param onError Callback de error
   */
  static getMyGroupVacation(
    year: number,
    month: number,
    onSuccess: SuccessCallback<ProcedureVacationGroup>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericGet(
      `user/groupVacation/${year}/${month}`,
      onSuccess,
      onError
    );
  }

  /**
   * Obtiene las vacaciones del empleado actual para un año completo
   * @param year Año (ej: 2024)
   * @param onSuccess Callback de éxito con datos agrupados de vacaciones
   * @param onError Callback de error
   */
  static getMyVacationYear(
    year: number,
    onSuccess: SuccessCallback<ProcedureVacationGroup>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericGet(
      `user/myVacation/${year}`,
      onSuccess,
      onError
    );
  }

  /**
   * Obtiene las vacaciones del equipo del empleado para un mes específico
   * @param year Año (ej: 2024)
   * @param month Mes (1-12)
   * @param onSuccess Callback de éxito con lista de vacaciones
   * @param onError Callback de error
   */
  static getMyTeamVacation(
    year: number,
    month: number,
    onSuccess: SuccessCallback<FullVacation[]>,
    onError: ErrorCallback
  ): void {
    worktimeApi.genericGet(
      `user/teamVacation/${year}/${month}`,
      onSuccess,
      onError
    );
  }

  /**
   * Crea una fecha simple a partir de año, mes y día
   * @param year Año
   * @param month Mes (1-12)
   * @param day Día
   */
  static createSimpleDate(year: number, month: number, day: number): SimpleDate {
    return { year, month, day };
  }

  /**
   * Crea un rango de fechas
   * @param fromYear Año inicial
   * @param fromMonth Mes inicial (1-12)
   * @param fromDay Día inicial
   * @param toYear Año final
   * @param toMonth Mes final (1-12)
   * @param toDay Día final
   */
  static createDateRange(
    fromYear: number,
    fromMonth: number,
    fromDay: number,
    toYear: number,
    toMonth: number,
    toDay: number
  ): SimpleDateRange {
    return {
      fromDate: this.createSimpleDate(fromYear, fromMonth, fromDay),
      toDate: this.createSimpleDate(toYear, toMonth, toDay)
    };
  }

  /**
   * Crea un rango de fechas para un mes completo
   * @param year Año
   * @param month Mes (1-12)
   */
  static createMonthDateRange(year: number, month: number): SimpleDateRange {
    const lastDay = new Date(year, month, 0).getDate();
    return this.createDateRange(year, month, 1, year, month, lastDay);
  }

  /**
   * Crea un rango de fechas para un año completo
   * @param year Año
   */
  static createYearDateRange(year: number): SimpleDateRange {
    return this.createDateRange(year, 1, 1, year, 12, 31);
  }
}

// Exportar instancia única (singleton)
export const employeeCalendarApi = new employeeCalendarAPI();

// Exportar como default para compatibilidad
export default employeeCalendarApi;